import {
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
} from "@mui/icons-material";
import { FC, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay/hooks";

import { SourceTableDataRow_source$key } from "~/src/__relay_artifacts__/SourceTableDataRow_source.graphql";
import {
  TableDataColumn,
  TableDataRow,
} from "~/src/components/common/tables/Table";
import { TableActions } from "~/src/components/common/tables/TableActions";
import { useOpenState } from "~/src/lib/hooks";

import { SourceDeleteConfirmDialog } from "./SourceDeleteConfirmDialog";
import { StyledDescriptionDataColumn } from "./styles";

export type Props = {
  sourceRef: SourceTableDataRow_source$key;
  siteSlug: string;
  isAdmin: boolean;
  onDeleteClick: (sourceSlug: string) => void;
};

const sourceFragment = graphql`
  fragment SourceTableDataRow_source on Source {
    id
    name
    description
    tag
    slug
    associatedAbtestGroupMessages
    associatedPopupGroupMessages
    associatedChatbotGroupMessages
  }
`;

export const SourceTableDataRow: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ sourceRef, siteSlug, isAdmin, onDeleteClick }) => {
  const { isOpen, onOpen, onClose } = useOpenState();
  const source = useFragment(sourceFragment, sourceRef);

  const handleDeleteConfirm = useCallback(() => {
    onDeleteClick(source.id);
  }, [onDeleteClick, source.id]);

  const handleDeleteClick = useCallback(() => {
    onOpen();
  }, [onOpen]);

  const actionMenus = useMemo(() => {
    const editMenu = {
      label: "編集",
      icon: <EditOutlinedIcon />,
      to: `/sites/${siteSlug}/sources/${source.slug}/edit`,
    };
    if (!isAdmin) {
      return [editMenu];
    }
    return [
      editMenu,
      {
        label: "削除",
        icon: <DeleteOutlineIcon />,
        onClick: () => handleDeleteClick(),
      },
    ];
  }, [siteSlug, source.slug, isAdmin, handleDeleteClick]);

  return (
    <>
      <TableDataRow>
        <TableDataColumn type="text" data={source.name} />
        <TableDataColumn type="text" data={source.tag} />
        <StyledDescriptionDataColumn type="text" data={source.description} />
        <TableDataColumn
          type="actions"
          data={<TableActions actionMenus={actionMenus} />}
        />
      </TableDataRow>
      {isOpen && (
        <SourceDeleteConfirmDialog
          name={source.name}
          associatedAbtestGroupMessages={source.associatedAbtestGroupMessages}
          associatedPopupGroupMessages={source.associatedPopupGroupMessages}
          associatedChatbotGroupMessages={source.associatedChatbotGroupMessages}
          isOpen={isOpen}
          onClose={onClose}
          onDelete={handleDeleteConfirm}
        />
      )}
    </>
  );
};
